import { setTokenGetter } from '@/api/client';
import { clearPendingSession, setSessionUserId } from '@/hooks';
import { analytics } from '@/lib/analytics';
import { useAuth0 } from '@auth0/auth0-react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  isDevLoginActive,
  getDevLoginToken,
  clearDevLogin,
  subscribeDevLogin,
} from './DevLoginButton';

// ============================================
// Auth Context
// ============================================

interface AuthContextValue {
  /** Whether Auth0 has loaded */
  isAuth0Loaded: boolean;
  /** Whether user is signed in (Auth0 or Dev Login) */
  isSignedIn: boolean;
  /** Whether token system is ready (tokenGetter is set) */
  isTokenReady: boolean;
  /** Whether authenticated requests can be made (all conditions met) */
  canMakeAuthenticatedRequests: boolean;
  /** Whether in Dev Login mode */
  isDevMode: boolean;
  /** Logout from Dev Login or Auth0 */
  logout: () => void;
  /** User info from Auth0 */
  user: {
    id?: string;
    email?: string;
    name?: string;
    picture?: string;
  } | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Auth0 API audience
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE;

// ============================================
// Auth Provider
// ============================================

/**
 * AuthProvider - Authentication state provider
 *
 * Problems solved:
 * - Auth0's getAccessTokenSilently needs to be injected into API client
 * - useEffect is async, requests may be sent before tokenGetter is set
 *
 * Solutions:
 * 1. Set tokenGetter when Auth0 loads
 * 2. Provide canMakeAuthenticatedRequests state to ensure all conditions are met before requests
 *
 * Also handles:
 * 3. Sync Analytics user identification with auth state
 * 4. Support Dev Login mode (quick login for development)
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const {
    isLoading,
    isAuthenticated,
    user,
    getAccessTokenSilently,
    logout: auth0Logout,
  } = useAuth0();

  const [isTokenReady, setIsTokenReady] = useState(false);

  // Dev Login state (use state to trigger re-render on change)
  const [devLoginState, setDevLoginState] = useState(() => ({
    isDevMode: isDevLoginActive(),
    devToken: getDevLoginToken(),
  }));

  // Subscribe to Dev Login state changes
  useEffect(() => {
    const unsubscribe = subscribeDevLogin(() => {
      setDevLoginState({
        isDevMode: isDevLoginActive(),
        devToken: getDevLoginToken(),
      });
    });
    return unsubscribe;
  }, []);

  const { isDevMode, devToken } = devLoginState;

  // Track previous auth state to detect sign-out
  const wasSignedInRef = useRef<boolean | null>(null);

  // Set tokenGetter - create a wrapper that gets Auth0 access token
  useEffect(() => {
    if (!isDevMode && !isLoading) {
      // Create a token getter function that matches the expected signature
      const tokenGetter = async () => {
        if (!isAuthenticated) return null;
        try {
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: AUTH0_AUDIENCE,
            },
          });
          return token;
        } catch (error) {
          // Handle refresh token errors - user needs to re-login
          const errorMessage = error instanceof Error ? error.message : String(error);
          const isRefreshTokenError = 
            errorMessage.includes('Missing Refresh Token') ||
            errorMessage.includes('invalid_grant') ||
            errorMessage.includes('Token is expired') ||
            errorMessage.includes('login_required');
          
          if (isRefreshTokenError) {
            console.warn('[Auth] Session expired, redirecting to login...');
            // Clear local state and redirect to login
            auth0Logout({
              logoutParams: {
                returnTo: `${window.location.origin}/sign-in`,
              },
            });
            return null;
          }
          
          console.error('[Auth] Failed to get access token:', error);
          return null;
        }
      };
      setTokenGetter(tokenGetter);
      setIsTokenReady(true);
    }
  }, [isLoading, isAuthenticated, getAccessTokenSilently, isDevMode, auth0Logout]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      setTokenGetter(null);
    };
  }, []);

  // Extract user info from Auth0 user or access token claims
  const userInfo = user
    ? {
        id: user.sub, // Auth0 user ID
        email: user.email,
        name: user.name,
        picture: user.picture,
      }
    : null;

  // Sync analytics user identification with auth state
  useEffect(() => {
    // Analytics handling for Dev Login mode
    if (isDevMode && devToken) {
      setSessionUserId('dev-user');
      clearPendingSession();

      analytics.identify('dev-user');
      analytics.setUserProperties({
        $name: 'Dev User',
        is_dev_user: true,
      });
      console.log('[Analytics] Dev user identified (will be filtered)');
      return;
    }

    if (isLoading) return;

    const wasSignedIn = wasSignedInRef.current;

    if (isAuthenticated && userInfo) {
      // User signed in - set session user ID
      setSessionUserId(userInfo.id || 'unknown');

      // Clear pending session on new login
      if (wasSignedIn === false) {
        clearPendingSession();
        console.log('[SessionStorage] Cleared pending session on login');
      }

      // Identify in analytics
      analytics.identify(userInfo.id || 'unknown');
      analytics.setUserProperties({
        $name: userInfo.name || undefined,
        $email: userInfo.email || undefined,
        $avatar: userInfo.picture || undefined,
        auth0_id: userInfo.id,
      });

      // Track login event only on state transition
      if (wasSignedIn === false) {
        analytics.track('auth_login', {
          method: 'auth0',
          user_id: userInfo.id,
        });
        console.log('[Analytics] User logged in:', userInfo.id);
      } else {
        console.log('[Analytics] User identified:', userInfo.id);
      }
    } else if (wasSignedIn === true && !isAuthenticated) {
      // User signed out
      setSessionUserId(null);
      clearPendingSession();
      console.log('[SessionStorage] Cleared pending session on logout');

      analytics.reset();
      console.log('[Analytics] User signed out, analytics reset');
    }

    wasSignedInRef.current = isAuthenticated;
  }, [isLoading, isAuthenticated, userInfo, isDevMode, devToken]);

  // Logout function (handles both Dev Login and Auth0)
  const logout = () => {
    setSessionUserId(null);
    clearPendingSession();
    console.log('[SessionStorage] Cleared pending session on logout');

    if (isDevMode) {
      clearDevLogin();
      window.location.href = '/sign-in';
    } else {
      auth0Logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }
  };

  // Calculate effective sign-in state
  const effectiveSignedIn = isDevMode || isAuthenticated;
  const canMakeRequests = isDevMode
    ? !!devToken
    : !isLoading && isAuthenticated && isTokenReady;

  const value: AuthContextValue = {
    isAuth0Loaded: !isLoading,
    isSignedIn: effectiveSignedIn,
    isTokenReady: isDevMode ? true : isTokenReady,
    canMakeAuthenticatedRequests: canMakeRequests,
    isDevMode,
    logout,
    user: isDevMode
      ? { id: 'dev-user', name: 'Dev User', email: 'dev@studyjoyful.local' }
      : userInfo,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================
// Hook
// ============================================

/**
 * useAuthContext - Get authentication context
 *
 * @example
 * ```tsx
 * const { canMakeAuthenticatedRequests, user } = useAuthContext();
 *
 * const { data } = useQuery({
 *   queryKey: ['data'],
 *   queryFn: fetchData,
 *   enabled: canMakeAuthenticatedRequests,
 * });
 * ```
 */
export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
}

export default AuthProvider;
